/**
 * 分享 token 的编解码。
 *
 * 分享链接长这样：https://<域名><basePath>/s/<token>
 * （当前 basePath 是 /web，见 share.config.mjs 的 site.basePath）
 * token 是「payload 的 base64url(UTF-8 JSON)」，不含签名——因为它只承载
 * 「要打开 App 的哪个页面 + 卡片上显示什么」，不承载任何权限。
 *
 * 安全性依赖两点，缺一不可：
 *   1. 解码后必须校验 path 是否落在 shareLink.allowedPathPrefixes 白名单里
 *      （见 isAllowedInAppPath），否则别人可以伪造成任意跳转；
 *   2. 卡片上的标题/描述一律当纯文本渲染，不解析 HTML。
 *
 * 之所以用 base64url 而不是明文 query，是因为链接要发到微信/QQ 里，
 * 明文里的中文和 & 很容易被二次转义搞坏；base64url 只有 [A-Za-z0-9_-]，
 * 在任何聊天工具里都不会被改写。
 */

import { shareLink } from '../config.js';

const MAX_TOKEN_LENGTH = 2048;
const MAX_TEXT_LENGTH = 120;
const MAX_URL_LENGTH = 1024;

/* ------------------------------------------------------------------ */
/* base64url <-> UTF-8 字节                                            */
/* ------------------------------------------------------------------ */

function bytesToBase64Url(bytes) {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlToBytes(value) {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

/* ------------------------------------------------------------------ */
/* 字段清洗                                                            */
/* ------------------------------------------------------------------ */

/** 只允许站内相对路径：不能有协议、不能 `//` 开头、不能含 `..`。 */
export function normalizeInAppPath(rawPath) {
  if (typeof rawPath !== 'string') return '';

  let path = rawPath.trim();
  if (!path) return '';

  // 带协议的绝对地址一律拒绝，避免出现 `javascript:` / `intent:` 之类的东西
  if (/^[a-z][a-z0-9+.-]*:/i.test(path)) return '';
  if (path.startsWith('//')) return '';

  if (!path.startsWith('/')) path = `/${path}`;

  // 折叠多余的斜杠，拒绝路径穿越
  path = path.replace(/\/{2,}/g, '/');
  if (/(^|\/)\.\.?($|\/)/.test(path)) return '';

  return path;
}

/** 分享的落地路径是否在允许的 App 路由白名单内。 */
export function isAllowedInAppPath(rawPath) {
  const path = normalizeInAppPath(rawPath);
  if (!path || path === '/') return false;

  const firstSegment = path.split('/').filter(Boolean)[0] ?? '';
  return shareLink.allowedPathPrefixes.includes(firstSegment);
}

/** 标题/描述只保留纯文本，并截断到合理长度。 */
function cleanText(value, maxLength = MAX_TEXT_LENGTH) {
  if (typeof value !== 'string') return '';
  // 去掉控制字符（含换行），卡片上一律单行展示
  const text = value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim();
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
}

/** 图片只接受 http(s) 或站内相对路径，挡掉 data:/javascript:。 */
function cleanImageUrl(value) {
  if (typeof value !== 'string') return '';
  const url = value.trim();
  if (!url || url.length > MAX_URL_LENGTH) return '';
  if (url.startsWith('/')) return url.startsWith('//') ? '' : url;
  if (/^https?:\/\//i.test(url)) return url;
  return '';
}

/* ------------------------------------------------------------------ */
/* 编解码                                                              */
/* ------------------------------------------------------------------ */

/**
 * 把分享内容编码成 token。
 *
 * @param {{ path: string, title?: string, description?: string, image?: string, author?: string }} share
 * @returns {string} base64url token；输入非法时返回空串
 */
export function encodeShareToken(share) {
  const path = normalizeInAppPath(share?.path);
  if (!path) return '';

  const payload = { p: path };

  const title = cleanText(share?.title);
  if (title) payload.t = title;

  const description = cleanText(share?.description, 80);
  if (description) payload.d = description;

  const image = cleanImageUrl(share?.image);
  if (image) payload.i = image;

  const author = cleanText(share?.author, 40);
  if (author) payload.n = author;

  // 短键名 + 压缩后的 JSON，让链接尽量短（微信里链接越长越容易被折叠）
  return bytesToBase64Url(new TextEncoder().encode(JSON.stringify(payload)));
}

/**
 * 解码分享 token。
 * 任何异常都返回 null，由调用方把这类链接当成失效链接处理。
 *
 * @param {string} token
 * @returns {{ path: string, title: string, description: string, image: string, author: string, valid: boolean } | null}
 */
export function decodeShareToken(token) {
  if (typeof token !== 'string') return null;

  const trimmed = token.trim();
  if (!trimmed || trimmed.length > MAX_TOKEN_LENGTH) return null;

  // 只允许 base64url 字符集，也顺便挡掉 path 里夹带的斜杠
  if (!/^[A-Za-z0-9_-]+$/.test(trimmed)) return null;

  let payload;
  try {
    const json = new TextDecoder().decode(base64UrlToBytes(trimmed));
    payload = JSON.parse(json);
  } catch {
    return null;
  }

  if (!payload || typeof payload !== 'object') return null;

  const path = normalizeInAppPath(payload.p);

  return {
    path,
    title: cleanText(payload.t),
    description: cleanText(payload.d, 80),
    image: cleanImageUrl(payload.i),
    author: cleanText(payload.n, 40),
    // 路径不合法时仍然返回对象（卡片还能显示），但 valid=false 表示跳转不可用
    valid: Boolean(path) && isAllowedInAppPath(path),
  };
}
